<template>
  <!-- KOL申请弹窗 -->
  <transition name="fade" mode="out-in">
    <div class="popup feedback-popup kol-popup" v-show="visible">
      <div class="popup-mask" @click="closePopup"></div>
      <div class="popup-content">
        <div class="popup-content-close" @click="closePopup"></div>
        <div class="popup-title">
          <div class="popup-title-icon"></div>
          <div class="popup-title-text">KOL申請</div>
        </div>
        <div class="content-area">
          <!-- 當前帳號 -->
          <div class="form-item">
            <label class="form-label">當前帳號：</label>
            <div class="account-info">{{ currentAccount }}</div>
          </div>

          <!-- 電子郵箱 -->
          <div class="form-item">
            <label class="form-label required">電子郵箱：</label>
            <input
              class="form-input"
              type="email"
              v-model="formData.email"
              placeholder="請輸入聯繫郵箱"
              required
            />
          </div>

          <!-- Discord用戶名 -->
          <div class="form-item">
            <label class="form-label required"> Discord用戶名： </label>
            <input
              class="form-input"
              type="text"
              v-model="formData.discordUserName"
              placeholder="請輸入Discord用戶名"
              required
            />
            <img
              class="question-icon"
              src="@images/popup/question.png"
              alt="?"
              @click="showDiscordHelp = true"
            />
          </div>

          <!-- 創作專長 -->
          <div class="form-item">
            <label class="form-label required">創作專長：</label>
            <div class="select-box">
              <select v-model="formData.specialtyType" required>
                <option value="" disabled hidden>請選擇創作專長</option>
                <option value="1">攻略撰寫</option>
                <option value="2">美術設計</option>
                <option value="3">影片製作</option>
                <option value="4">其他</option>
              </select>
            </div>
          </div>

          <!-- 過往作品連結 -->
          <div class="form-item form-item-vertical">
            <label class="form-label required">過往作品連結：</label>
            <textarea
              class="text-input"
              v-model="formData.workUrlList"
              placeholder="請輸入過往作品的連結地址，多個連結請用逗號分隔"
              maxlength="1000"
              required
            ></textarea>
            <div class="char-count">{{ formData.workUrlList.length }}/1000</div>
          </div>

          <!-- 訂閱者數量 -->
          <div class="form-item">
            <label class="form-label required">訂閱者數量：</label>
            <input
              class="form-input"
              type="number"
              v-model="formData.followerTotal"
              placeholder="請輸入當前訂閱者數量"
              min="0"
              required
            />
          </div>
        </div>

        <div class="popup-bottom">
          <div
            class="submit-btn"
            @click="submitApplication"
            :class="{ disabled: isSubmitting }"
          ></div>
          <div
            class="cancel-btn"
            @click="closePopup"
            :class="{ disabled: isSubmitting }"
          ></div>
        </div>
      </div>
    </div>
  </transition>

  <!-- Discord帮助弹窗 -->
  <transition name="fade" mode="out-in">
    <div
      class="discord-help-popup"
      v-show="showDiscordHelp"
      @click="showDiscordHelp = false"
    >
      <div class="discord-help-content" @click.stop>
        <img
          class="discord-help-image"
          src="@images/popup/dicord-popup.png"
          alt="Discord用户名说明"
        />
        <div class="discord-help-close" @click="showDiscordHelp = false">×</div>
      </div>
    </div>
  </transition>

  <!-- 退出确认弹窗 -->
  <exitConfirmPopup
    :visible="showExitConfirm"
    @update:visible="showExitConfirm = $event"
    @confirm="handleExitConfirm"
  />
</template>

<script setup>
import { ref, reactive, watch, inject, computed } from "vue";
import { getJSON } from "@api/request";
import { getUrlParams, getActivityApi } from "@utils";
import LanguageHook from "@hooks/language";
import exitConfirmPopup from "./exitConfirmPopup.vue";

// 定义 props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  userInfo: {
    type: Object,
    default: () => ({}),
  },
});

// 定义 emits
const emit = defineEmits(["update:visible", "showTip"]);

// 语言相关
const { langData } = LanguageHook();

// 响应式数据
const formData = reactive({
  email: "",
  discordUserName: "",
  specialtyType: "",
  workUrlList: "",
  followerTotal: "",
});

const isSubmitting = ref(false);
const showDiscordHelp = ref(false);
const showExitConfirm = ref(false);

// 计算当前账号显示
const currentAccount = computed(() => {
  if (props.userInfo && props.userInfo.username) {
    return `${props.userInfo.username || ""}`;
  }
  return "未登录";
});

// 重置表单
const reset = () => {
  formData.email = "";
  formData.discordUserName = "";
  formData.specialtyType = "";
  formData.workUrlList = "";
  formData.followerTotal = 0;
};

// 监听弹窗显示状态
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      // 弹窗打开时可以做一些初始化操作
      console.log("KOL申请弹窗已打开");
    }
  }
);

// 检查表单是否有内容
const hasFormData = () => {
  return (
    formData.email ||
    formData.discordUserName ||
    formData.specialtyType ||
    formData.workUrlList ||
    formData.followerTotal
  );
};

// 关闭弹窗 - 如果有表单内容则显示确认弹窗
const closePopup = (showConfirm = true) => {
  if (showConfirm) {
    showExitConfirm.value = true;
  } else {
    // 直接关闭
    emit("update:visible", false);
  }
};

// 确认退出处理
const handleExitConfirm = () => {
  reset();
  emit("update:visible", false);
};

// 提交申请
const submitApplication = async () => {
  // 验证必填项
  if (!formData.email) {
    emit("showTip", "請輸入電子郵箱");
    return;
  }

  // 验证邮箱格式
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email)) {
    emit("showTip", "請輸入正確的電子郵箱格式");
    return;
  }

  if (!formData.discordUserName) {
    emit("showTip", "請輸入Discord用戶名");
    return;
  }

  if (!formData.specialtyType) {
    emit("showTip", "請選擇創作專長");
    return;
  }

  if (!formData.workUrlList || formData.workUrlList.trim() === "") {
    emit("showTip", "請輸入過往作品連結");
    return;
  }

  // 验证订阅者数量为正整数
  const followerNum = parseInt(formData.followerTotal);
  if (isNaN(followerNum) || followerNum < 0) {
    emit("showTip", "請輸入有效的訂閱者數量");
    return;
  }

  try {
    window.activityDataLayer.push({
      eventName: "click_button",
      eventType: "KOL申請",
      eventReason: "KOL申請",
    });
    // 显示提交中状态
    isSubmitting.value = true;

    // 准备提交数据
    const params = {
      email: formData.email,
      discordUserName: encodeURIComponent(
        encodeURIComponent(formData.discordUserName)
      ),
      specialtyType: formData.specialtyType,
      workUrlList: encodeURIComponent(formData.workUrlList), // 必填字段
      followerTotal: followerNum.toString(),
    };

    console.log("提交KOL申请参数:", params);

    // 调用KOL申请API
    const { data, result, code } = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "joinTeam"),
      {
        params: JSON.stringify(params),
      }
    );

    console.log("KOL申请API响应:", { data, result, code });

    if (+result === 1) {
      emit("showTip", "申請提交成功");
      // 清空表单
      reset();
      closePopup(false);
    } else {
      emit(
        "showTip",
        `[${code}]${
          langData[Math.abs(+code)] ||
          data?.message ||
          "申請提交失敗，請稍後重試"
        }`
      );
    }
  } catch (error) {
    console.error("提交KOL申請異常:", error);
    emit("showTip", "申請提交失敗，請檢查網絡連接後重試");
  } finally {
    isSubmitting.value = false;
  }
};
</script>
